import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ProductionOrderResponse } from '@/modules/shift-management/types/production-order.type';
import type { ShiftPayload, ShiftResponse } from '@/modules/shift-management/types/shift.type';
import { computeIsOvernight } from '@/modules/shift-management/types/shift.type';
import type {
    AssignmentHistoryEntry,
    AssignmentUser,
    DailyOverride,
    ShiftAssignmentResponse,
} from '@/modules/shift-management/types/shift-assignment.type';

// ─── Mock users ───────────────────────────────────────────────────────────────

export const MOCK_USERS: AssignmentUser[] = [
    {
        id: 'user-1',
        fullname: 'Nguyễn Văn An',
        email: 'an.nguyen@company.com',
        position: 'Kỹ thuật viên',
    },
    {
        id: 'user-2',
        fullname: 'Trần Thị Bình',
        email: 'binh.tran@company.com',
        position: 'Kỹ thuật viên',
    },
    {
        id: 'user-3',
        fullname: 'Lê Văn Cường',
        email: 'cuong.le@company.com',
        position: 'Giám sát ca',
    },
    {
        id: 'user-4',
        fullname: 'Phạm Thị Dung',
        email: 'dung.pham@company.com',
        position: 'Vận hành máy',
    },
    {
        id: 'user-5',
        fullname: 'Hoàng Minh Đức',
        email: 'duc.hoang@company.com',
        position: 'Kỹ thuật viên',
    },
];

// ─── Tracking template ────────────────────────────────────────────────────────

const TRACKING = {
    createdBy: 'system',
    modifiedBy: null as string | null,
    modifiedDate: null as string | null,
    isDeleted: false,
    deletedAt: null as string | null,
    deletedBy: null as string | null,
};

// ─── Mock shifts ──────────────────────────────────────────────────────────────

const mockShifts: ShiftResponse[] = [
    {
        id: 'shift-1',
        name: 'Ca sáng',
        startTime: '06:00',
        endTime: '14:00',
        isOvernight: false,
        isActive: true,
        sortOrder: 1,
        createdDate: '2026-01-01T00:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'shift-2',
        name: 'Ca chiều',
        startTime: '14:00',
        endTime: '22:00',
        isOvernight: false,
        isActive: true,
        sortOrder: 2,
        createdDate: '2026-01-01T00:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'shift-3',
        name: 'Ca đêm',
        startTime: '22:00',
        endTime: '06:00',
        isOvernight: true,
        isActive: true,
        sortOrder: 3,
        createdDate: '2026-01-01T00:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'shift-4',
        name: 'Ca hành chính',
        startTime: '08:00',
        endTime: '17:00',
        isOvernight: false,
        isActive: false,
        sortOrder: 4,
        createdDate: '2026-01-01T00:00:00.000Z',
        ...TRACKING,
    },
];

// ─── Mock production orders ───────────────────────────────────────────────────

const mockProductionOrders: ProductionOrderResponse[] = [
    {
        id: 'po-001',
        code: 'LSX-2026-001',
        name: 'Sản xuất linh kiện điện tử A',
        description: 'Sản xuất 10.000 linh kiện loại A cho dây chuyền lắp ráp',
        status: 'IN_PROGRESS',
        startDate: '2026-03-01T00:00:00.000Z',
        endDate: '2026-03-31T00:00:00.000Z',
        shiftIds: ['shift-1', 'shift-2', 'shift-3'],
        numberOfShifts: 3,
        isActive: true,
        createdDate: '2026-02-20T08:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'po-002',
        code: 'LSX-2026-002',
        name: 'Sản xuất linh kiện cơ khí B',
        description: 'Sản xuất khung thép định hình cho module B',
        status: 'DRAFT',
        startDate: '2026-04-01T00:00:00.000Z',
        endDate: '2026-04-30T00:00:00.000Z',
        shiftIds: ['shift-1', 'shift-2'],
        numberOfShifts: 2,
        isActive: true,
        createdDate: '2026-03-10T09:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'po-003',
        code: 'LSX-2026-003',
        name: 'Kiểm tra chất lượng batch X',
        description: 'QC toàn bộ batch sản phẩm X trước khi xuất kho',
        status: 'COMPLETED',
        startDate: '2026-02-01T00:00:00.000Z',
        endDate: '2026-02-28T00:00:00.000Z',
        shiftIds: ['shift-1'],
        numberOfShifts: 1,
        isActive: false,
        createdDate: '2026-01-25T10:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'po-004',
        code: 'LSX-2026-004',
        name: 'Sản xuất linh kiện nhựa C',
        description: 'Đúc khuôn linh kiện nhựa chịu nhiệt',
        status: 'CANCELLED',
        startDate: '2026-03-15T00:00:00.000Z',
        endDate: '2026-03-20T00:00:00.000Z',
        shiftIds: ['shift-1', 'shift-2'],
        numberOfShifts: 2,
        isActive: false,
        createdDate: '2026-03-05T11:00:00.000Z',
        ...TRACKING,
    },
];

// ─── Mock assignments ─────────────────────────────────────────────────────────

const mockAssignments: ShiftAssignmentResponse[] = [
    // PO-001: Ca 1 & Ca 2 assigned, Ca 3 unassigned
    {
        id: 'asgn-po1-s1',
        productionOrderId: 'po-001',
        shiftId: 'shift-1',
        shiftName: 'Ca sáng',
        shiftStartTime: '06:00',
        shiftEndTime: '14:00',
        sortOrder: 1,
        userId: 'user-1',
        user: MOCK_USERS[0],
        effectiveFrom: '2026-03-01T06:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: 'Phân công ban đầu',
        createdDate: '2026-02-20T08:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'asgn-po1-s2',
        productionOrderId: 'po-001',
        shiftId: 'shift-2',
        shiftName: 'Ca chiều',
        shiftStartTime: '14:00',
        shiftEndTime: '22:00',
        sortOrder: 2,
        userId: 'user-2',
        user: MOCK_USERS[1],
        effectiveFrom: '2026-03-01T14:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: null,
        createdDate: '2026-02-20T08:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'asgn-po1-s3',
        productionOrderId: 'po-001',
        shiftId: 'shift-3',
        shiftName: 'Ca đêm',
        shiftStartTime: '22:00',
        shiftEndTime: '06:00',
        sortOrder: 3,
        userId: null,
        user: null,
        effectiveFrom: '2026-03-01T22:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: null,
        createdDate: '2026-02-20T08:00:00.000Z',
        ...TRACKING,
    },
    // PO-002: both unassigned
    {
        id: 'asgn-po2-s1',
        productionOrderId: 'po-002',
        shiftId: 'shift-1',
        shiftName: 'Ca sáng',
        shiftStartTime: '06:00',
        shiftEndTime: '14:00',
        sortOrder: 1,
        userId: null,
        user: null,
        effectiveFrom: '2026-04-01T06:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: null,
        createdDate: '2026-03-10T09:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'asgn-po2-s2',
        productionOrderId: 'po-002',
        shiftId: 'shift-2',
        shiftName: 'Ca chiều',
        shiftStartTime: '14:00',
        shiftEndTime: '22:00',
        sortOrder: 2,
        userId: null,
        user: null,
        effectiveFrom: '2026-04-01T14:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: null,
        createdDate: '2026-03-10T09:00:00.000Z',
        ...TRACKING,
    },
    // PO-003: 1 shift assigned
    {
        id: 'asgn-po3-s1',
        productionOrderId: 'po-003',
        shiftId: 'shift-1',
        shiftName: 'Ca sáng',
        shiftStartTime: '06:00',
        shiftEndTime: '14:00',
        sortOrder: 1,
        userId: 'user-3',
        user: MOCK_USERS[2],
        effectiveFrom: '2026-02-01T06:00:00.000Z',
        effectiveTo: '2026-02-28T14:00:00.000Z',
        isActive: false,
        notes: 'Đã hoàn thành',
        createdDate: '2026-01-25T10:00:00.000Z',
        ...TRACKING,
    },
];

// ─── Mock history ─────────────────────────────────────────────────────────────

const mockHistory: AssignmentHistoryEntry[] = [
    {
        id: 'hist-001',
        productionOrderId: 'po-001',
        shiftId: 'shift-1',
        shiftName: 'Ca sáng',
        userId: 'user-3',
        user: MOCK_USERS[2],
        effectiveFrom: '2026-03-01T06:00:00.000Z',
        effectiveTo: '2026-03-10T06:00:00.000Z',
        isActive: false,
        notes: 'Phân công ban đầu — Lê Văn Cường nghỉ phép',
        createdDate: '2026-02-20T08:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'hist-002',
        productionOrderId: 'po-001',
        shiftId: 'shift-1',
        shiftName: 'Ca sáng',
        userId: 'user-1',
        user: MOCK_USERS[0],
        effectiveFrom: '2026-03-10T06:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: 'Đổi sang Nguyễn Văn An do Lê Văn Cường nghỉ phép',
        createdDate: '2026-03-10T07:00:00.000Z',
        ...TRACKING,
    },
    {
        id: 'hist-003',
        productionOrderId: 'po-001',
        shiftId: 'shift-2',
        shiftName: 'Ca chiều',
        userId: 'user-2',
        user: MOCK_USERS[1],
        effectiveFrom: '2026-03-01T14:00:00.000Z',
        effectiveTo: null,
        isActive: true,
        notes: null,
        createdDate: '2026-02-20T08:00:00.000Z',
        ...TRACKING,
    },
];

// ─── Mock daily overrides ─────────────────────────────────────────────────────

const mockDailyOverrides: DailyOverride[] = [
    {
        id: 'override-1',
        productionOrderId: 'po-001',
        shiftId: 'shift-1',
        date: '2026-03-15',
        userId: 'user-4',
        user: MOCK_USERS[3],
        reason: 'Nguyễn Văn An nghỉ phép ngày này',
        createdDate: '2026-03-14T08:00:00.000Z',
        createdBy: 'mock-user',
    },
    {
        id: 'override-2',
        productionOrderId: 'po-001',
        shiftId: 'shift-1',
        date: '2026-03-22',
        userId: 'user-5',
        user: MOCK_USERS[4],
        reason: 'Tăng cường nhân sự cuối tháng',
        createdDate: '2026-03-20T09:00:00.000Z',
        createdBy: 'mock-user',
    },
    {
        id: 'override-3',
        productionOrderId: 'po-001',
        shiftId: 'shift-2',
        date: '2026-03-20',
        userId: 'user-3',
        user: MOCK_USERS[2],
        reason: 'Trần Thị Bình đi công tác',
        createdDate: '2026-03-19T10:00:00.000Z',
        createdBy: 'mock-user',
    },
];

// ─── State ────────────────────────────────────────────────────────────────────

export interface ShiftManagementState {
    shifts: ShiftResponse[];
    productionOrders: ProductionOrderResponse[];
    assignments: ShiftAssignmentResponse[];
    history: AssignmentHistoryEntry[];
    dailyOverrides: DailyOverride[];
}

const initialState: ShiftManagementState = {
    shifts: mockShifts,
    productionOrders: mockProductionOrders,
    assignments: mockAssignments,
    history: mockHistory,
    dailyOverrides: mockDailyOverrides,
};

// ─── ID generator ─────────────────────────────────────────────────────────────

function generateId(prefix: string) {
    return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const shiftManagementSlice = createSlice({
    name: 'shiftManagement',
    initialState,
    reducers: {
        // ── Shift CRUD ────────────────────────────────────────────────
        addShift(state, action: PayloadAction<ShiftPayload>) {
            const now = new Date().toISOString();
            const maxOrder = state.shifts.reduce(
                (max, s) => Math.max(max, s.sortOrder),
                0
            );
            const newShift: ShiftResponse = {
                id: generateId('shift'),
                name: action.payload.name,
                startTime: action.payload.startTime,
                endTime: action.payload.endTime,
                isOvernight: computeIsOvernight(
                    action.payload.startTime,
                    action.payload.endTime
                ),
                isActive: action.payload.isActive,
                sortOrder: action.payload.sortOrder ?? maxOrder + 1,
                createdDate: now,
                createdBy: 'mock-user',
                modifiedDate: null,
                modifiedBy: null,
                isDeleted: false,
                deletedAt: null,
                deletedBy: null,
            };
            state.shifts.push(newShift);
        },

        updateShift(
            state,
            action: PayloadAction<{ id: string; payload: ShiftPayload }>
        ) {
            const { id, payload } = action.payload;
            const idx = state.shifts.findIndex(s => s.id === id);
            if (idx !== -1) {
                state.shifts[idx] = {
                    ...state.shifts[idx],
                    ...payload,
                    isOvernight: computeIsOvernight(
                        payload.startTime,
                        payload.endTime
                    ),
                    modifiedDate: new Date().toISOString(),
                };
            }
        },

        deleteShifts(state, action: PayloadAction<string[]>) {
            const ids = new Set(action.payload);
            state.shifts = state.shifts.filter(s => !ids.has(s.id));
        },

        toggleShiftActive(state, action: PayloadAction<string>) {
            const idx = state.shifts.findIndex(s => s.id === action.payload);
            if (idx !== -1) {
                state.shifts[idx].isActive = !state.shifts[idx].isActive;
                state.shifts[idx].modifiedDate = new Date().toISOString();
            }
        },

        // ── Production Order CRUD ─────────────────────────────────────
        addProductionOrder(
            state,
            action: PayloadAction<
                Omit<
                    ProductionOrderResponse,
                    | 'id'
                    | 'createdDate'
                    | 'modifiedDate'
                    | 'modifiedBy'
                    | 'createdBy'
                    | 'isDeleted'
                    | 'deletedAt'
                    | 'deletedBy'
                    | 'numberOfShifts'
                    | 'shifts'
                >
            >
        ) {
            const now = new Date().toISOString();
            const newPO: ProductionOrderResponse = {
                id: generateId('po'),
                createdDate: now,
                createdBy: 'mock-user',
                modifiedDate: null,
                modifiedBy: null,
                isDeleted: false,
                deletedAt: null,
                deletedBy: null,
                numberOfShifts: action.payload.shiftIds.length,
                ...action.payload,
            };
            state.productionOrders.unshift(newPO);

            // Auto-create empty assignment slots for each selected shift
            const selectedShifts = state.shifts.filter(s =>
                newPO.shiftIds.includes(s.id)
            );

            selectedShifts.forEach((shift, idx) => {
                state.assignments.push({
                    id: generateId('asgn'),
                    productionOrderId: newPO.id,
                    shiftId: shift.id,
                    shiftName: shift.name,
                    shiftStartTime: shift.startTime,
                    shiftEndTime: shift.endTime,
                    sortOrder: shift.sortOrder ?? idx + 1,
                    userId: null,
                    user: null,
                    effectiveFrom: newPO.startDate,
                    effectiveTo: null,
                    isActive: true,
                    notes: null,
                    createdDate: now,
                    createdBy: 'mock-user',
                    modifiedDate: null,
                    modifiedBy: null,
                    isDeleted: false,
                    deletedAt: null,
                    deletedBy: null,
                });
            });
        },

        updateProductionOrder(
            state,
            action: PayloadAction<{
                id: string;
                changes: Partial<ProductionOrderResponse>;
            }>
        ) {
            const { id, changes } = action.payload;
            const idx = state.productionOrders.findIndex(po => po.id === id);
            if (idx !== -1) {
                const updated = {
                    ...state.productionOrders[idx],
                    ...changes,
                    numberOfShifts:
                        changes.shiftIds?.length ??
                        state.productionOrders[idx].numberOfShifts,
                    modifiedDate: new Date().toISOString(),
                };
                state.productionOrders[idx] = updated;

                // Re-sync assignment slots when shifts change
                if (changes.shiftIds) {
                    const newShiftIds = new Set(changes.shiftIds);
                    const existingShiftIds = new Set(
                        state.assignments
                            .filter(
                                a =>
                                    a.productionOrderId === id && a.isActive
                            )
                            .map(a => a.shiftId)
                    );

                    // Add slots for new shifts
                    const now = new Date().toISOString();
                    changes.shiftIds.forEach(shiftId => {
                        if (!existingShiftIds.has(shiftId)) {
                            const shift = state.shifts.find(
                                s => s.id === shiftId
                            );
                            if (shift) {
                                state.assignments.push({
                                    id: generateId('asgn'),
                                    productionOrderId: id,
                                    shiftId: shift.id,
                                    shiftName: shift.name,
                                    shiftStartTime: shift.startTime,
                                    shiftEndTime: shift.endTime,
                                    sortOrder: shift.sortOrder,
                                    userId: null,
                                    user: null,
                                    effectiveFrom: updated.startDate,
                                    effectiveTo: null,
                                    isActive: true,
                                    notes: null,
                                    createdDate: now,
                                    createdBy: 'mock-user',
                                    modifiedDate: null,
                                    modifiedBy: null,
                                    isDeleted: false,
                                    deletedAt: null,
                                    deletedBy: null,
                                });
                            }
                        }
                    });

                    // Deactivate removed shifts
                    state.assignments
                        .filter(
                            a =>
                                a.productionOrderId === id &&
                                a.isActive &&
                                !newShiftIds.has(a.shiftId)
                        )
                        .forEach(a => {
                            a.isActive = false;
                        });
                }
            }
        },

        deleteProductionOrders(state, action: PayloadAction<string[]>) {
            const ids = new Set(action.payload);
            state.productionOrders = state.productionOrders.filter(
                po => !ids.has(po.id)
            );
            state.assignments = state.assignments.filter(
                a => !ids.has(a.productionOrderId)
            );
        },

        // ── Shift Assignment ──────────────────────────────────────────
        assignUser(
            state,
            action: PayloadAction<{
                productionOrderId: string;
                shiftId: string;
                user: AssignmentUser;
                effectiveFrom: string;
                notes?: string;
            }>
        ) {
            const { productionOrderId, shiftId, user, effectiveFrom, notes } =
                action.payload;
            const now = new Date().toISOString();
            const idx = state.assignments.findIndex(
                a =>
                    a.productionOrderId === productionOrderId &&
                    a.shiftId === shiftId &&
                    a.isActive
            );
            if (idx !== -1) {
                state.assignments[idx] = {
                    ...state.assignments[idx],
                    userId: user.id,
                    user,
                    effectiveFrom,
                    notes: notes ?? null,
                    modifiedDate: now,
                };
                state.history.push({
                    ...state.assignments[idx],
                    id: generateId('hist'),
                    createdDate: now,
                });
            }
        },

        // ── Daily Overrides ───────────────────────────────────────
        addDailyOverride(
            state,
            action: PayloadAction<{
                productionOrderId: string;
                shiftId: string;
                date: string;
                user: AssignmentUser;
                reason: string;
            }>
        ) {
            const { productionOrderId, shiftId, date, user, reason } =
                action.payload;
            // Replace existing override for same (PO, shift, date) if any
            state.dailyOverrides = state.dailyOverrides.filter(
                o =>
                    !(
                        o.productionOrderId === productionOrderId &&
                        o.shiftId === shiftId &&
                        o.date === date
                    )
            );
            state.dailyOverrides.push({
                id: generateId('override'),
                productionOrderId,
                shiftId,
                date,
                userId: user.id,
                user,
                reason,
                createdDate: new Date().toISOString(),
                createdBy: 'mock-user',
            });
        },

        deleteDailyOverride(state, action: PayloadAction<string>) {
            state.dailyOverrides = state.dailyOverrides.filter(
                o => o.id !== action.payload
            );
        },

        changeUser(
            state,
            action: PayloadAction<{
                assignmentId: string;
                user: AssignmentUser;
                effectiveFrom: string;
                notes?: string;
            }>
        ) {
            const { assignmentId, user, effectiveFrom, notes } = action.payload;
            const now = new Date().toISOString();
            const idx = state.assignments.findIndex(
                a => a.id === assignmentId
            );
            if (idx !== -1) {
                const old = state.assignments[idx];

                state.history.push({
                    ...old,
                    id: generateId('hist'),
                    effectiveTo: effectiveFrom,
                    isActive: false,
                    createdDate: now,
                    modifiedDate: null,
                    modifiedBy: null,
                });

                state.assignments[idx] = {
                    ...old,
                    userId: user.id,
                    user,
                    effectiveFrom,
                    notes: notes ?? null,
                    modifiedDate: now,
                };

                state.history.push({
                    ...state.assignments[idx],
                    id: generateId('hist'),
                    createdDate: now,
                    modifiedDate: null,
                    modifiedBy: null,
                });
            }
        },
    },
});

export const {
    addShift,
    updateShift,
    deleteShifts,
    toggleShiftActive,
    addProductionOrder,
    updateProductionOrder,
    deleteProductionOrders,
    assignUser,
    changeUser,
    addDailyOverride,
    deleteDailyOverride,
} = shiftManagementSlice.actions;

export default shiftManagementSlice.reducer;
